import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle2, TrendingUp, Award, Sparkles, IndianRupee, Users, Megaphone, Package, Wrench, Building2, Star } from "lucide-react";
import { FRANCHISE_MODELS, FRANCHISE_BENEFITS } from "../lib/siteData";
import { submitLead } from "../lib/api";
import { toast } from "sonner";
import CTABanner from "../components/CTABanner";
import SEO from "../components/SEO";

export default function BusinessFranchisePage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", franchise_type: "", investment_capacity: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.phone || !form.franchise_type) {
      toast.error("Please fill name, phone and franchise type");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...form, source: "franchise" });
      toast.success("Application received! Our franchise team will call you within 24 hours.");
      setForm({ name: "", phone: "", email: "", city: "", franchise_type: "", investment_capacity: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="franchise-page">
      <SEO title="Business Franchise" description="Start your own solar business with CNGTPL Solar franchise. Dealer, Distributor & EPC Partner models. High ROI, zero royalty, end-to-end training." />
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-blue-900 via-slate-900 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(ellipse at top right, #2563eb 0%, transparent 50%)" }} />
        <div className="container-narrow relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-600/40 text-blue-300 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5">
              <Sparkles className="w-3.5 h-3.5" /> 150+ partners across India
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05] mb-5">
              Start your own <span className="text-yellow-400">solar business</span>
            </h1>
            <p className="text-lg text-slate-300 max-w-xl mb-6">
              Join India's fastest growing solar brand. Zero royalty, high margins, proven systems. Whether you want to be a Dealer, Distributor or EPC Partner — we make it easy.
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              {[
                { icon: TrendingUp, label: "High ROI" },
                { icon: Award, label: "Zero Royalty" },
                { icon: Users, label: "Training Support" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2">
                  <b.icon className="w-5 h-5 text-yellow-400" />
                  <span className="text-slate-200 font-medium">{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 text-slate-900 shadow-2xl">
            <div className="text-xs uppercase tracking-widest text-blue-700 font-bold mb-1">Apply now</div>
            <h3 className="font-heading text-2xl font-semibold mb-4">Become a franchise partner</h3>
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="franchise-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="franchise-name" />
                <Input placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="franchise-phone" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="franchise-email" />
                <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="franchise-city" />
              </div>
              <Select value={form.franchise_type} onValueChange={(v) => setForm({ ...form, franchise_type: v })}>
                <SelectTrigger data-testid="franchise-type"><SelectValue placeholder="Franchise Type *" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dealer">Solar Dealer</SelectItem>
                  <SelectItem value="Distributor">Solar Distributor</SelectItem>
                  <SelectItem value="EPC Partner">EPC / Installer Partner</SelectItem>
                </SelectContent>
              </Select>
              <Select value={form.investment_capacity} onValueChange={(v) => setForm({ ...form, investment_capacity: v })}>
                <SelectTrigger data-testid="franchise-investment"><SelectValue placeholder="Investment Capacity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-3L">₹1 - 3 Lakh</SelectItem>
                  <SelectItem value="3-5L">₹3 - 5 Lakh</SelectItem>
                  <SelectItem value="5-10L">₹5 - 10 Lakh</SelectItem>
                  <SelectItem value="10L+">₹10 Lakh+</SelectItem>
                </SelectContent>
              </Select>
              <Textarea placeholder="Tell us about your business experience" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="franchise-message" />
              <Button type="submit" disabled={submitting} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 text-base py-6" data-testid="franchise-submit">
                {submitting ? "Submitting..." : "Apply Now"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Why partner */}
      <section className="section-pad">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <div className="badge-eco mb-3 mx-auto">Why Partner with us</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">Built for entrepreneurs</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FRANCHISE_BENEFITS.map((b, i) => {
              const Icon = [TrendingUp, IndianRupee, Users, Megaphone, Package, Award][i] || Star;
              return (
                <div key={b.title} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-blue-400 hover:shadow-lg transition">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Models */}
      <section className="section-pad bg-slate-50">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <div className="badge-eco mb-3 mx-auto">Franchise Models</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">Choose the right model for you</h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">From entry-level dealerships to full EPC partnerships — pick a model that matches your investment and ambition.</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            {FRANCHISE_MODELS.map((m) => {
              const Icon = m.name.includes("Dealer") ? Building2 : m.name.includes("Distributor") ? Package : Wrench;
              return (
                <div
                  key={m.name}
                  className={`rounded-2xl p-8 border-2 transition ${m.highlighted ? "bg-slate-900 text-white border-blue-600 shadow-2xl lg:scale-105" : "bg-white border-slate-200 hover:border-blue-400"}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${m.highlighted ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700"}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${m.highlighted ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-800"}`}>
                      {m.badge}
                    </span>
                  </div>
                  <h3 className={`font-heading text-2xl font-bold mb-2 ${m.highlighted ? "text-white" : "text-slate-900"}`}>{m.name}</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className={`stat-number text-3xl font-bold ${m.highlighted ? "text-blue-400" : "text-blue-700"}`}>{m.investment}</span>
                  </div>
                  <div className={`text-xs uppercase tracking-widest font-semibold mb-4 ${m.highlighted ? "text-slate-400" : "text-slate-500"}`}>Investment • {m.area}</div>
                  <p className={`text-sm mb-5 leading-relaxed ${m.highlighted ? "text-slate-300" : "text-slate-600"}`}>{m.description}</p>
                  <ul className="space-y-2 mb-6">
                    {m.features.map((f) => (
                      <li key={f} className={`flex gap-2 text-sm ${m.highlighted ? "text-slate-300" : "text-slate-600"}`}>
                        <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${m.highlighted ? "text-blue-400" : "text-blue-600"}`} /> {f}
                      </li>
                    ))}
                  </ul>
                  <a href="#top" onClick={(e) => { e.preventDefault(); setForm((f) => ({ ...f, franchise_type: m.name.includes("Dealer") ? "Dealer" : m.name.includes("Distributor") ? "Distributor" : "EPC Partner" })); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={`block text-center font-semibold py-3 px-5 rounded-lg transition w-full ${m.highlighted ? "bg-blue-600 hover:bg-blue-400 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`} data-testid={`model-cta-${m.name.replace(/\s/g, "-")}`}>
                    {m.cta}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CTABanner title="Ready to grow with us?" subtitle="Fill the application above and our franchise manager will walk you through next steps." primaryLabel="Talk to Franchise Team" primaryTo="/contact" />
    </div>
  );
}
